// The house pill-tab switcher — a centred group of Cormorant pills on a foil wash.
//
// Extracted from the library's own tab bar so My Books can use the identical control
// instead of a third variant (the guide's rule: never hand-roll another). My Books adds
// per-tab counts, which is the only thing that differs, so `count` is optional.
//
// A real `role="tablist"` with ARROW-KEY ROVING FOCUS. `<button>` rather than
// `<a role="tab">`: the URL stays in sync so a tab is linkable, but announcing these as
// links would tell assistive tech they navigate when they don't.
//
// Deliberately NOT sticky. Two of these on a page would need a two-level sticky stack and
// a translucent ground, and alpha over the ruled paper is forbidden for content.

import { useRef } from "react";

export interface PillTab<K extends string> {
  key: K;
  label: string;
  /** Rendered as a counter bubble. Omit for tabs that aren't collections of things. */
  count?: number;
}

interface PillTabBarProps<K extends string> {
  tabs: PillTab<K>[];
  active: K;
  onChange: (key: K) => void;
  /** Names the tablist for assistive tech, e.g. "Library sections". */
  ariaLabel: string;
  /** The quiet mono line under the pills. */
  meta?: string;
  /** Fires on hover/focus so a parent can prefetch that tab's chunk or data. */
  onIntent?: (key: K) => void;
  /** Prefix for the `id` / `aria-controls` pair, e.g. "lib" → `lib-panel-store`. */
  idPrefix: string;
}

export function PillTabBar<K extends string>({
  tabs,
  active,
  onChange,
  ariaLabel,
  meta,
  onIntent,
  idPrefix,
}: PillTabBarProps<K>) {
  const refs = useRef<(HTMLButtonElement | null)[]>([]);

  function onKeyDown(event: React.KeyboardEvent, index: number) {
    const last = tabs.length - 1;
    let next: number | null = null;
    if (event.key === "ArrowRight") next = index === last ? 0 : index + 1;
    else if (event.key === "ArrowLeft") next = index === 0 ? last : index - 1;
    else if (event.key === "Home") next = 0;
    else if (event.key === "End") next = last;
    if (next === null) return;
    event.preventDefault();
    onChange(tabs[next].key);
    refs.current[next]?.focus();
  }

  return (
    <div className="flex w-full flex-col items-center gap-3 pt-8 sm:pt-9">
      {/* `w-full` on the scroller, not `max-w-full`: the parent is an `items-center`
          column, so it sizes to its content — a percentage max-width would resolve
          against a width the child itself defines and clamp nothing, letting the pills
          stretch the whole document. At `sm` the scroller hugs its content again. */}
      <div
        role="tablist"
        aria-label={ariaLabel}
        className="flex w-full items-center gap-1 overflow-x-auto no-scrollbar sm:w-auto sm:overflow-visible sm:rounded-full sm:bg-foil-wash sm:px-1.5 sm:py-1.5"
      >
        {tabs.map((tab, index) => {
          const selected = tab.key === active;
          return (
            <button
              key={tab.key}
              ref={(node) => { refs.current[index] = node; }}
              role="tab"
              id={`${idPrefix}-tab-${tab.key}`}
              aria-selected={selected}
              aria-controls={`${idPrefix}-panel-${tab.key}`}
              tabIndex={selected ? 0 : -1}
              onClick={() => onChange(tab.key)}
              onKeyDown={(event) => onKeyDown(event, index)}
              onMouseEnter={() => onIntent?.(tab.key)}
              onFocus={() => onIntent?.(tab.key)}
              className={[
                "inline-flex shrink-0 items-center gap-2 whitespace-nowrap rounded-full",
                "px-5 py-2.5 font-serif text-[19px] leading-none sm:px-6 sm:py-3 sm:text-[21px]",
                "transition-colors duration-200 ease-observatory",
                selected
                  ? "bg-terracotta text-paper shadow-btn-terra"
                  : "text-ink-faint hover:text-terracotta",
              ].join(" ")}
            >
              {tab.label}
              {tab.count !== undefined && (
                <span
                  className={[
                    "inline-flex h-[19px] min-w-[20px] items-center justify-center rounded-full px-1.5",
                    // `lining-nums`: globals.css sets `onum` on body, which renders a 0 as
                    // a lowercase o inside a small bubble.
                    "font-mono text-[10px] lining-nums tabular-nums",
                    selected ? "bg-paper/25 text-paper" : "bg-paper-deep text-ink-dim",
                  ].join(" ")}
                >
                  {tab.count}
                </span>
              )}
            </button>
          );
        })}
      </div>
      {meta && (
        <span className="font-mono text-[10px] uppercase tracking-smallcaps lining-nums tabular-nums text-foil">
          {meta}
        </span>
      )}
    </div>
  );
}
